import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, LogOut, Package } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { getMyOrders, getMyWishlist, removeFromWishlist, getRoleContent } from "@/lib/api";

const badgeCls = (s) =>
  ({
    preparing: "bg-[#FFB56B]/20 text-[#9A5A12]",
    shipped: "bg-[#7BC5D8]/20 text-[#1F6B80]",
    in_transit: "bg-[#B6A6E0]/25 text-[#4B3B8C]",
    out_for_delivery: "bg-[#FF8473]/20 text-[#B3402F]",
    ready_for_pickup: "bg-[#D7E85C]/30 text-[#5C6B12]",
  }[s] || "bg-[#1F3864]/10 text-[#1F3864]");

export default function Account() {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState({ products: [] });
  const [tab, setTab] = useState("orders");
  const [roleContent, setRoleContent] = useState(null);
  // Priority: admin email > account role (from DB) > stored role from sign-in > buyer
  const role = user?.is_admin
    ? "admin"
    : (user?.role && user.role !== "admin" ? user.role : localStorage.getItem("rada_role")) || "buyer";

  useEffect(() => {
    if (!user) return;
    getMyOrders().then(setOrders).catch(() => {});
    getMyWishlist().then(setWishlist).catch(() => {});
    getRoleContent().then(setRoleContent).catch(() => {});
  }, [user]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#1F3864]"><div className="text-[#EBE6E0] animate-pulse font-bold">Loading…</div></div>;
  }

  if (!user) {
    return (
      <div data-testid="account-login-gate" className="min-h-screen grid place-items-center bg-[#1F3864] px-6">
        <div className="max-w-md w-full text-center">
          <img src="/rada-logo-white.png" alt="Rada Collab" className="h-8 mx-auto mb-8" />
          <h1 className="font-display font-bold text-3xl text-[#EBE6E0] mb-3">Your account</h1>
          <p className="text-[#EBE6E0]/60 mb-8">Sign in to track your orders and saved pieces.</p>
          <button data-testid="account-login-btn" onClick={() => login()}
            className="w-full bg-[#D7E85C] text-[#1F3864] font-bold uppercase tracking-wide px-6 py-4 rounded-full hover:bg-[#EBE6E0] transition-colors">
            Sign in with Google
          </button>
          <button onClick={() => navigate("/")} className="mt-4 inline-flex items-center gap-2 text-sm text-[#EBE6E0]/50 hover:text-[#EBE6E0]">
            <ArrowLeft size={14} /> Back to site
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="account-page" className="min-h-screen bg-[#F4F1EC]">
      <header className="bg-[#1F3864] text-[#EBE6E0]">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-bold">
            <img src="/rada-logo-white.png" alt="Rada Collab" className="h-6" />
          </button>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-[#D7E85C]" />}
            <span className="text-sm font-bold hidden sm:block" data-testid="account-user-name">{user.name}</span>
            <button data-testid="account-logout-btn" onClick={logout} className="p-2 rounded-full bg-white/10 hover:bg-[#FF8473] hover:text-[#1F3864] transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10">
        <div className="mb-6">
          <h1 className="font-display font-bold text-3xl text-[#1F3864]">
            {roleContent?.[role]?.title || `Hi, ${user.name?.split(" ")[0] || "there"}`}
          </h1>
          {roleContent?.[role]?.subtitle && (
            <p className="text-[#1F3864]/60 mt-1" data-testid="account-role-subtitle">{roleContent[role].subtitle}</p>
          )}
          <span className="mt-2 inline-block text-xs font-bold uppercase tracking-wider text-[#00AF95]" data-testid="account-role">
            {role}
          </span>
        </div>

        <div className="flex gap-2 mb-8">
          {[["orders", `My Orders (${orders.length})`, Package], ["saved", `Saved (${wishlist.products?.length || 0})`, Heart]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`account-tab-${id}`} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                tab === id ? "bg-[#1F3864] text-[#D7E85C]" : "bg-white text-[#1F3864] border border-[#1F3864]/20"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === "orders" ? (
          orders.length === 0 ? (
            <div data-testid="orders-empty" className="bg-white rounded-2xl border border-[#1F3864]/10 p-12 text-center">
              <Package size={32} className="mx-auto text-[#1F3864]/30 mb-3" />
              <p className="text-[#1F3864]/50 text-sm">No orders yet. Back the campaign to see it here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} data-testid={`my-order-${o.id}`} className="bg-white rounded-2xl border border-[#1F3864]/10 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div>
                      <div className="font-bold text-[#1F3864]">{o.package_name || "Order"} · {o.currency === "php" ? "₱" : "$"}{o.amount}</div>
                      <div className="text-xs text-[#1F3864]/50">
                        {new Date(o.created_at).toLocaleDateString()}
                        {o.shipping_method === "pickup" && o.pickup_location_name && <> · Pick up at {o.pickup_location_name}</>}
                        {o.shipping_method === "ship" && o.shipping_address && <> · Ships to {o.shipping_address}</>}
                      </div>
                    </div>
                    <span data-testid={`my-order-status-${o.id}`} className={`px-3 py-1.5 rounded-full text-xs font-bold ${badgeCls(o.status)}`}>
                      {o.status_label || o.status}
                    </span>
                  </div>
                  {o.history?.length > 0 && (
                    <div className="border-t border-[#1F3864]/10 pt-3 space-y-1.5">
                      {[...o.history].reverse().map((h, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-[#1F3864]/70 font-bold">{h.label}</span>
                          <span className="text-[#1F3864]/40">{new Date(h.at).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          wishlist.products?.length === 0 ? (
            <div data-testid="wishlist-empty" className="bg-white rounded-2xl border border-[#1F3864]/10 p-12 text-center">
              <Heart size={32} className="mx-auto text-[#1F3864]/30 mb-3" />
              <p className="text-[#1F3864]/50 text-sm">No saved pieces yet. Tap the heart on any product.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {wishlist.products.map((p) => (
                <div key={p.id} data-testid={`wishlist-item-${p.id}`} className="bg-white rounded-2xl border border-[#1F3864]/10 overflow-hidden">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.title} className="w-full h-40 object-cover" />}
                  <div className="p-4">
                    <div className="font-bold text-[#1F3864]">{p.title}</div>
                    <div className="text-sm text-[#1F3864]/60 mb-3">${p.price_usd} / ₱{p.price_php}</div>
                    <button data-testid={`wishlist-remove-${p.id}`}
                      onClick={() => removeFromWishlist(p.id).then(() => getMyWishlist().then(setWishlist))}
                      className="text-xs font-bold text-[#FF8473] hover:underline">
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}
